/**
 * Gastos de actividad de una oportunidad (dietas, transporte, alojamiento…).
 * Lista los Expense vinculados a la oportunidad y permite alta rápida con
 * concepto por chips, importe TOTAL (IVA incl.) y fase de imputación:
 * PREVENTA suma al CAC, EJECUCION al coste de entrega (decisión 07/2026).
 * La contabilización (asiento de devengo/pago) la hace el backend como en
 * cualquier gasto; la categoría por defecto es VIAJES_DIETAS.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Loader2, Pencil, Plus, Receipt, Trash2 } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { confirmDialog, toast } from '../../lib/dialogs'
import Modal from '../ui/Modal'
import { currentLocaleTag } from '../../lib/formatting'
import { formatCents, parseEurosToCents } from '../../lib/money'
import { useExpenseCategories } from '../../lib/expense-categories'
import {
  TimeEntryPhaseLabel,
  type ExpenseDto,
  type ExpenseListResponse,
  type TimeEntryPhaseT,
} from '@toga/shared'

interface Props {
  opportunityId: string
  /** Fase de ejecución actual (null = pipeline) para la fase por defecto. */
  executionPhase: string | null
  canWrite: boolean
  /** Notifica cambios (para refrescar la rentabilidad de la ficha). */
  onChanged?: () => void
}

const CONCEPT_CHIPS = ['breakfast', 'lunch', 'dinner', 'transport', 'lodging'] as const

/**
 * Base imponible cuyo recálculo del backend (vat = round(base × rate);
 * total = base + vat) reproduce EXACTAMENTE el total del ticket. Se explora
 * ±2 céntimos alrededor de la división directa; si ningún candidato casa
 * (total sin preimagen exacta con ese tipo), cae a la división redondeada
 * (desvío máximo de 1 céntimo, inevitable).
 */
function deriveSubtotalCents(totalCents: number, vatRate: number): number {
  const approx = Math.round(totalCents / (1 + vatRate))
  for (let d = 0; d <= 2; d++) {
    for (const candidate of d === 0 ? [approx] : [approx - d, approx + d]) {
      if (candidate > 0 && candidate + Math.round(candidate * vatRate) === totalCents) {
        return candidate
      }
    }
  }
  return approx
}

export default function CaseExpensesPanel({
  opportunityId,
  executionPhase,
  canWrite,
  onChanged,
}: Props) {
  const { t } = useTranslation(['opportunities', 'common'])
  const [items, setItems] = useState<ExpenseDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState<{ initial: ExpenseDto | null } | null>(null)
  const { nameOf } = useExpenseCategories()

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get<ExpenseListResponse>(
        `/expenses?opportunityId=${opportunityId}&pageSize=100`,
      )
      setItems(resp.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error')
    } finally {
      setLoading(false)
    }
  }, [opportunityId])

  useEffect(() => {
    void reload()
  }, [reload])

  const totalCents = items
    .filter((e) => e.status !== 'ANULADO')
    .reduce((s, e) => s + e.totalCents, 0)

  const onDelete = async (e: ExpenseDto) => {
    if (!(await confirmDialog({ message: t('expensesPanel.confirmDelete'), tone: 'danger' }))) return
    try {
      await api.delete(`/expenses/${e.id}`)
      await reload()
      onChanged?.()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      toast.error(body?.error ?? 'Error')
    }
  }

  return (
    <div className="surface p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="t-primary font-semibold flex items-center gap-2">
          <Receipt size={14} className="text-toga-700 dark:text-toga-300" />
          {t('expensesPanel.title')}
          {items.length > 0 && (
            <span className="text-xs t-faint font-normal">({formatCents(totalCents)})</span>
          )}
        </h3>
        {canWrite && (
          <button
            type="button"
            onClick={() => setShowForm({ initial: null })}
            className="px-2.5 py-1 rounded-lg text-xs font-medium text-toga-700 dark:text-toga-200 bg-toga-500/15 hover:bg-toga-500/25 flex items-center gap-1"
          >
            <Plus size={12} /> {t('expensesPanel.add')}
          </button>
        )}
      </div>

      {error && (
        <div className="text-xs text-rose-700 dark:text-rose-300 flex items-center gap-1 mb-2">
          <AlertCircle size={11} /> {error}
        </div>
      )}

      {loading ? (
        <div className="t-faint text-xs flex items-center gap-1.5">
          <Loader2 size={11} className="animate-spin" /> {t('common:loading')}
        </div>
      ) : items.length === 0 ? (
        <p className="text-xs t-faint">{t('expensesPanel.empty')}</p>
      ) : (
        <ul className="divide-y divide-base/40 text-sm">
          {items.map((e) => (
            <li key={e.id} className="py-2 flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="t-primary">{e.concept}</div>
                <div className="flex items-center gap-2 text-[11px] t-muted mt-0.5 flex-wrap">
                  <span>{new Date(e.expenseDate).toLocaleDateString(currentLocaleTag())}</span>
                  <span className="font-mono">{formatCents(e.totalCents)}</span>
                  <span>· {nameOf(e.category)}</span>
                  {e.phase && <ExpensePhaseBadge phase={e.phase} />}
                  <ExpenseStatusBadge status={e.status} />
                </div>
              </div>
              {canWrite && e.status === 'PENDIENTE' && (
                <div className="flex items-center gap-0.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => setShowForm({ initial: e })}
                    className="w-6 h-6 grid place-items-center rounded hover:bg-hover t-muted hover:text-toga-700 dark:hover:text-toga-200"
                    title={t('common:actions.edit')}
                  >
                    <Pencil size={11} />
                  </button>
                  <button
                    type="button"
                    onClick={() => void onDelete(e)}
                    className="w-6 h-6 grid place-items-center rounded hover:bg-rose-500/10 t-muted hover:text-rose-400"
                    title={t('common:actions.delete')}
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <ExpenseQuickFormModal
          opportunityId={opportunityId}
          executionPhase={executionPhase}
          initial={showForm.initial}
          onClose={() => setShowForm(null)}
          onSaved={async () => {
            setShowForm(null)
            await reload()
            onChanged?.()
          }}
        />
      )}
    </div>
  )
}

function ExpenseStatusBadge({ status }: { status: ExpenseDto['status'] }) {
  const STYLES: Record<ExpenseDto['status'], string> = {
    PENDIENTE: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/40',
    PAGADO: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
    ANULADO: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30',
  }
  const { t } = useTranslation('opportunities')
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] border ${STYLES[status]}`}>
      {t(`expensesPanel.status.${status}`)}
    </span>
  )
}

function ExpensePhaseBadge({ phase }: { phase: TimeEntryPhaseT }) {
  const STYLES: Record<TimeEntryPhaseT, string> = {
    PREVENTA: 'bg-purple-500/10 text-purple-700 dark:text-purple-200 border-purple-500/40',
    EJECUCION: 'bg-toga-500/10 text-toga-700 dark:text-toga-200 border-toga-500/30',
  }
  const { t } = useTranslation()
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] border ${STYLES[phase]}`}>
      {t('enums:TimeEntryPhase.' + phase)}
    </span>
  )
}

function ExpenseQuickFormModal({
  opportunityId,
  executionPhase,
  initial,
  onClose,
  onSaved,
}: {
  opportunityId: string
  executionPhase: string | null
  initial: ExpenseDto | null
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const { t } = useTranslation(['opportunities', 'common'])
  const { categories, nameOf } = useExpenseCategories()

  const [concept, setConcept] = useState(initial?.concept ?? '')
  const [expenseDate, setExpenseDate] = useState(
    (initial?.expenseDate ?? new Date().toISOString()).slice(0, 10),
  )
  // Importe TOTAL (IVA incluido), como en el ticket; el subtotal se deriva.
  const [totalEuros, setTotalEuros] = useState(
    initial ? (initial.totalCents / 100).toFixed(2).replace('.', ',') : '',
  )
  const [vatPct, setVatPct] = useState(initial ? String(Math.round(initial.vatRate * 100)) : '10')
  const [category, setCategory] = useState(initial?.category ?? 'VIAJES_DIETAS')
  const [supplierName, setSupplierName] = useState(initial?.supplierName ?? '')
  // Fase por defecto: en pipeline (sin fase de ejecución) es PREVENTA; en
  // proyecto en marcha, EJECUCION. Igual criterio que el CAC.
  const [phase, setPhase] = useState<TimeEntryPhaseT>(
    initial?.phase ?? (executionPhase ? 'EJECUCION' : 'PREVENTA'),
  )
  const [alreadyPaid, setAlreadyPaid] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const applyChip = (key: string) => {
    setConcept(t(`expensesPanel.concepts.${key}`))
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const totalCents = totalEuros.trim() ? parseEurosToCents(totalEuros) : 0
    if (totalCents <= 0) {
      setError(t('expensesPanel.errors.invalidAmount'))
      return
    }
    if (!concept.trim()) {
      setError(t('expensesPanel.errors.missingConcept'))
      return
    }
    const vatRate = Math.min(100, Math.max(0, Number(vatPct) || 0)) / 100
    // total = subtotal + round(subtotal × IVA) → buscamos la BASE cuyo recálculo
    // del backend reproduce EXACTAMENTE el total del ticket (la división directa
    // puede desviar 1 céntimo: 100,00€ al 21% → base 82,64 → total 99,99€).
    const subtotalCents = deriveSubtotalCents(totalCents, vatRate)
    setBusy(true)
    setError(null)
    try {
      const body = {
        expenseDate: new Date(`${expenseDate}T12:00:00`).toISOString(),
        supplierName: supplierName.trim() || concept.trim(),
        concept: concept.trim(),
        category,
        opportunityId,
        phase,
        subtotalCents,
        vatRate,
      }
      if (initial) {
        await api.patch(`/expenses/${initial.id}`, body)
      } else {
        const { expense } = await api.post<{ expense: ExpenseDto }>('/expenses', body)
        if (alreadyPaid) {
          // Dietas: normalmente ya pagadas al registrarlas. Marca el pago (y su
          // asiento) en el acto; si falla, el gasto queda PENDIENTE y avisamos
          // (el alta ya está hecha, no bloqueamos el guardado).
          try {
            // Sin amountCents: el servidor liquida el pendiente real (total −
            // IRPF − ya pagado); enviar totalCents descuadraría la 400 en gastos
            // con retención (L3 #35/#55).
            await api.post(`/expenses/${expense.id}/pay`, {
              paymentDate: body.expenseDate,
            })
          } catch {
            toast.error(t('expensesPanel.errors.payFailed'))
          }
        }
      }
      await onSaved()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setBusy(false)
    }
  }

  const input =
    'w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary focus:outline-none focus:border-toga-500/50'

  return (
    <Modal
      open
      onClose={onClose}
      title={initial ? t('expensesPanel.form.editTitle') : t('expensesPanel.form.newTitle')}
    >
      <form onSubmit={onSubmit} className="space-y-3">
        {/* Chips de concepto rápido */}
        <div className="flex flex-wrap gap-1.5">
          {CONCEPT_CHIPS.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => applyChip(key)}
              className="px-2 py-1 rounded-full text-[11px] border border-base t-secondary hover:bg-hover hover:border-toga-500/40"
            >
              {t(`expensesPanel.concepts.${key}`)}
            </button>
          ))}
        </div>
        <label className="block">
          <span className="text-xs t-muted">{t('expensesPanel.form.concept')}</span>
          <input
            value={concept}
            onChange={(e) => setConcept(e.target.value)}
            placeholder={t('expensesPanel.form.conceptPlaceholder')}
            className={input}
            required
            autoFocus
          />
        </label>
        <div className="grid grid-cols-3 gap-3">
          <label className="block">
            <span className="text-xs t-muted">{t('expensesPanel.form.date')}</span>
            <input
              type="date"
              value={expenseDate}
              onChange={(e) => setExpenseDate(e.target.value)}
              className={input}
              required
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('expensesPanel.form.total')}</span>
            <input
              value={totalEuros}
              onChange={(e) => setTotalEuros(e.target.value)}
              placeholder="25,50"
              inputMode="decimal"
              className={`${input} font-mono`}
              required
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('expensesPanel.form.vat')}</span>
            <select value={vatPct} onChange={(e) => setVatPct(e.target.value)} className={input}>
              <option value="10">10 %</option>
              <option value="21">21 %</option>
              <option value="0">0 %</option>
            </select>
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">{t('expensesPanel.form.phase')}</span>
            <select
              value={phase}
              onChange={(e) => setPhase(e.target.value as TimeEntryPhaseT)}
              className={input}
              title={t('expensesPanel.form.phaseHint')}
            >
              {Object.keys(TimeEntryPhaseLabel).map((k) => (
                <option key={k} value={k}>
                  {t('enums:TimeEntryPhase.' + k)}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('expensesPanel.form.category')}</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className={input}
            >
              {/* Si la categoría actual no está en el catálogo activo, la mostramos igualmente. */}
              {!categories.some((c) => c.code === category) && (
                <option value={category}>{nameOf(category)}</option>
              )}
              {categories.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="block">
          <span className="text-xs t-muted">{t('expensesPanel.form.supplier')}</span>
          <input
            value={supplierName}
            onChange={(e) => setSupplierName(e.target.value)}
            placeholder={t('expensesPanel.form.supplierPlaceholder')}
            className={input}
          />
        </label>
        {!initial && (
          <label className="flex items-center gap-2 text-sm t-secondary">
            <input
              type="checkbox"
              checked={alreadyPaid}
              onChange={(e) => setAlreadyPaid(e.target.checked)}
              className="accent-toga-500"
            />
            {t('expensesPanel.form.alreadyPaid')}
          </label>
        )}

        {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {initial ? t('common:actions.save') : t('expensesPanel.form.register')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
